/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { mergeDeep } from '../store/state/state-manager';

import type { Paging, DataPageRecord } from './types';

class Data {
  isDirty: boolean;
  private records: any[];
  private maxRecords: number;

  constructor() {
    this.records = [];
    this.maxRecords = 0;
    this.isDirty = false;
  }

  getRecords(paging?: Paging) {
    if (Number.isInteger(paging?.pageNumber) && Number.isInteger(paging?.pageSize)) {
      const results: any[] = [];
      const { pageNumber, pageSize } = paging as {
        pageNumber: number;
        pageSize: number;
      };
      const endIndex = pageNumber * pageSize;
      let dataIndex = endIndex - pageSize;
      for (; dataIndex < endIndex; dataIndex += 1) {
        if (!this.records[dataIndex]) {
          if (this.maxRecords === dataIndex) {
            return results;
          }
          return [];
        }
        results.push(this.records[dataIndex]);
      }
      return results;
    }
    return this.records;
  }

  mergeRecords(updatedRecords: DataPageRecord[], paging?: Paging) {
    if (Number.isInteger(paging?.pageNumber) && Number.isInteger(paging?.pageSize)) {
      const { pageNumber, pageSize } = paging as {
        pageNumber: number;
        pageSize: number;
      };
      const endIndex = pageNumber * pageSize;
      const startIndex = endIndex - pageSize + 1;
      let dataIndex = 0;
      for (let i = startIndex - 1; i < endIndex; i += 1) {
        this.records[i] = updatedRecords[dataIndex];
        dataIndex += 1;
      }

      if (updatedRecords.length < pageSize) {
        const maxRecords = (pageNumber - 1) * pageSize + updatedRecords.length;
        this.setMaxRecordsCount(maxRecords);
      }
    } else {
      if (!updatedRecords) updatedRecords = [];
      if (Array.isArray(this.records) && Array.isArray(updatedRecords)) {
        this.records = mergeDeep(this.records, updatedRecords, { isArrayDeepMerge: false });
      }
      this.setMaxRecordsCount(this.records.length);
    }
  }

  setMaxRecordsCount(count: number) {
    this.maxRecords = count;
  }

  getMaxRecordsCount() {
    return this.maxRecords;
  }
}

export default Data;
